var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useQuery } from '@tanstack/react-query';
import { useCallback, useState, useEffect, useMemo } from 'react';
import { ethers } from 'ethers';
import { TxStep } from '@sovryn/sdk';
import { useAccount } from '../../../../hooks';
import { isRskChain } from '../../../../utils/chain';
import { useBridgeService } from './useBridgeService';
import { useBridgeValidation } from './useBridgeValidation';
export function useBridge({ sourceChain, targetChain, asset, amount, receiver, onSuccess, onTxStart, }) {
    const bridgeService = useBridgeService();
    const { account, signer } = useAccount();
    const [transaction, setTransaction] = useState({
        step: TxStep.IDLE,
    });
    const bridgeConfig = useMemo(() => bridgeService === null || bridgeService === void 0 ? void 0 : bridgeService.getBridgeConfig(sourceChain, targetChain), [bridgeService, sourceChain, targetChain]);
    const assetConfig = useMemo(() => bridgeService === null || bridgeService === void 0 ? void 0 : bridgeService.getAssetConfig(sourceChain, targetChain, asset), [bridgeService, sourceChain, targetChain, asset]);
    const spenderAddress = useMemo(() => {
        if (!bridgeConfig || !assetConfig) {
            return undefined;
        }
        if (isRskChain(sourceChain) && assetConfig.usesAggregator) {
            return assetConfig.aggregatorContractAddress;
        }
        return bridgeConfig.bridgeContractAddress;
    }, [bridgeConfig, assetConfig, sourceChain]);
    // Fetch allowance
    const { data: allowance, refetch: refetchAllowance } = useQuery({
        queryKey: ['bridgeAllowance', sourceChain, asset, account, spenderAddress],
        queryFn: () => __awaiter(this, void 0, void 0, function* () {
            if (!bridgeService || !account || !spenderAddress) {
                throw new Error('Missing requirements');
            }
            return bridgeService.getAllowance(sourceChain, asset, account, spenderAddress);
        }),
        enabled: !!bridgeService &&
            !!account &&
            !!spenderAddress &&
            !(assetConfig === null || assetConfig === void 0 ? void 0 : assetConfig.isNative),
    });
    const requiresApproval = useMemo(() => {
        if (!assetConfig || assetConfig.isNative)
            return false;
        if (!allowance || !amount)
            return false;
        try {
            const amountBN = ethers.BigNumber.from(amount);
            const allowanceBN = ethers.BigNumber.from(allowance);
            return amountBN.gt(allowanceBN);
        }
        catch (_a) {
            return true;
        }
    }, [assetConfig, allowance, amount]);
    const { isValid } = useBridgeValidation({
        sourceChain,
        targetChain,
        asset,
        amount,
        receiver,
    });
    const handleApproval = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        if (!bridgeService || !signer || !spenderAddress) {
            throw new Error('Missing requirements for approval');
        }
        setTransaction({ step: TxStep.APPROVING });
        try {
            const tx = yield bridgeService.approve(sourceChain, asset, spenderAddress, amount.toString(), signer);
            const receipt = yield tx.wait();
            if (receipt.status === 0) {
                throw new Error('Approval transaction failed');
            }
            refetchAllowance();
            setTransaction({
                step: TxStep.APPROVED,
                approveHash: receipt.transactionHash,
            });
        }
        catch (error) {
            if ((error === null || error === void 0 ? void 0 : error.code) === 4001 || (error === null || error === void 0 ? void 0 : error.code) === 'ACTION_REJECTED') {
                setTransaction({ step: TxStep.USER_DENIED, error });
            }
            else {
                setTransaction({ step: TxStep.FAILED, error });
            }
            throw error;
        }
    }), [
        bridgeService,
        signer,
        spenderAddress,
        sourceChain,
        asset,
        amount,
        refetchAllowance,
    ]);
    const handleBridge = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        if (!bridgeService || !signer) {
            throw new Error('Missing requirements for bridge');
        }
        setTransaction(prev => (Object.assign(Object.assign({}, prev), { step: TxStep.CONFIRMING })));
        try {
            const tx = yield bridgeService.bridge({
                sourceChain,
                targetChain,
                asset,
                amount: amount.toString(),
                receiver: receiver || account,
                signer,
            });
            setTransaction(prev => (Object.assign(Object.assign({}, prev), { step: TxStep.PENDING, transferHash: tx.hash })));
            const receipt = yield tx.wait();
            if (receipt.status === 0) {
                throw new Error('Bridge transaction failed');
            }
            setTransaction(prev => (Object.assign(Object.assign({}, prev), { step: TxStep.CONFIRMED, transferHash: receipt.transactionHash })));
            onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess();
        }
        catch (error) {
            console.log({
                error,
            });
            if ((error === null || error === void 0 ? void 0 : error.code) === 4001 || (error === null || error === void 0 ? void 0 : error.code) === 'ACTION_REJECTED') {
                setTransaction(prev => (Object.assign(Object.assign({}, prev), { step: TxStep.USER_DENIED, error })));
            }
            else {
                setTransaction(prev => (Object.assign(Object.assign({}, prev), { step: TxStep.FAILED, error })));
            }
            throw error;
        }
    }), [
        bridgeService,
        signer,
        sourceChain,
        targetChain,
        asset,
        amount,
        receiver,
        account,
        onSuccess,
    ]);
    const handleSubmit = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        try {
            setTransaction({ step: TxStep.IDLE });
            if (!isValid) {
                throw new Error('Cannot proceed with bridge: insufficient balance or amount out of limits');
            }
            onTxStart === null || onTxStart === void 0 ? void 0 : onTxStart();
            if (requiresApproval) {
                yield handleApproval();
            }
            yield handleBridge();
        }
        catch (error) {
            console.error('Bridge transfer failed:', error);
        }
    }), [isValid, onTxStart, requiresApproval, handleBridge, handleApproval]);
    // Reset transaction when parameters change
    useEffect(() => {
        setTransaction({ step: TxStep.IDLE });
    }, [sourceChain, targetChain, asset]);
    return {
        handleSubmit,
        transaction,
        requiresApproval,
        isValid,
    };
}
